import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import { useApi, Spinner, ErrBanner, today, fmtDate } from '../../hooks/useApi';
import './Support.css';

const CATEGORIES = ['General Query','Billing Issue','Technical Problem','Feature Request','Data Issue','Other'];
const PRIORITIES  = ['Low','Medium','High','Critical'];

const STATUS_LABEL = { open:'Open', in_progress:'In Progress', resolved:'Resolved', closed:'Closed' };
const STATUS_CLASS = { open:'status-open', in_progress:'status-in_progress', resolved:'status-resolved', closed:'status-closed' };
const PRIORITY_CLASS = { Critical:'priority-critical', High:'priority-high', Medium:'priority-medium', Low:'priority-low' };


export default function Support() {
  const { user } = useAuth();

  const { data: tickets, loading, error, reload } = useApi(() => api.tickets.list(), []);
  const [view, setView]     = useState('list');   // 'list' | 'new' | 'detail'
  const [selected, setSel]  = useState(null);
  const [replyText, setRT]  = useState('');
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const notify = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 4000); };

  const [newForm, setNF] = useState({
    subject:'', category: CATEGORIES[0], priority:'Medium', description:''
  });

  /* ── Stats ── */
  const all        = tickets || [];
  const statOpen   = all.filter(t=>t.status==='open').length;
  const statIP     = all.filter(t=>t.status==='in_progress').length;
  const statRes    = all.filter(t=>t.status==='resolved').length;
  const statTotal  = all.length;

  /* ── Filtered list ── */
  const filtered = filter==='all' ? all : all.filter(t=>t.status===filter);

  /* ── Open a ticket ── */
  const openTicket = (t) => {
    setSel(t);
    setView('detail');
    setRT('');
  };

  /* ── Submit new ticket ── */
  const submitTicket = async (e) => {
    e.preventDefault();
    if (!newForm.subject.trim() || !newForm.description.trim()) {
      notify('❌ Subject and Description are required'); return;
    }
    setSaving(true);
    try {
      await api.tickets.create(newForm);
      notify('✅ Ticket submitted! We will respond shortly.');
      setNF({ subject:'', category:CATEGORIES[0], priority:'Medium', description:'' });
      setView('list');
      reload();
    } catch(e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  /* ── Send reply ── */
  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSaving(true);
    try {
      await api.tickets.reply(selected.id, { message: replyText });
      notify('✅ Reply sent');
      setRT('');
      // Refresh detail
      const updated = await api.tickets.fetch(selected.id);
      setSel(updated);
      reload();
    } catch(e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  /* ── Close ticket ── */
  const closeTicket = async (id) => {
    if (!window.confirm('Close this ticket?')) return;
    try {
      await api.tickets.updateStatus(id, 'closed');
      notify('✅ Ticket closed');
      setView('list');
      reload();
    } catch(e) { notify('❌ ' + e.message); }
  };

  return (
    <div className="support-page">
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title">🎫 Support & Tickets</h2>
        <div style={{display:'flex',gap:8}}>
          {view !== 'list' && (
            <button className="btn btn-secondary btn-sm" onClick={()=>setView('list')}>← Back to Tickets</button>
          )}
          {view === 'list' && (
            <button className="btn btn-primary btn-sm" onClick={()=>setView('new')}>＋ Raise New Ticket</button>
          )}
        </div>
      </div>

      {msg && <div className="notify-bar">{msg}</div>}

      {/* ── STATS ── */}
      {view === 'list' && (
        <div className="ticket-stats">
          <div className="ticket-stat-card">
            <div className="tsc-icon">🎫</div>
            <div><div className="tsc-label">Total Tickets</div><div className="tsc-value">{statTotal}</div></div>
          </div>
          <div className="ticket-stat-card">
            <div className="tsc-icon">🟡</div>
            <div><div className="tsc-label">Open</div><div className="tsc-value" style={{color:'#c05621'}}>{statOpen}</div></div>
          </div>
          <div className="ticket-stat-card">
            <div className="tsc-icon">🔵</div>
            <div><div className="tsc-label">In Progress</div><div className="tsc-value" style={{color:'#1a6fad'}}>{statIP}</div></div>
          </div>
          <div className="ticket-stat-card">
            <div className="tsc-icon">✅</div>
            <div><div className="tsc-label">Resolved</div><div className="tsc-value" style={{color:'#2d9e5f'}}>{statRes}</div></div>
          </div>
        </div>
      )}

      {/* ══════════════════════ LIST VIEW ══════════════════════ */}
      {view === 'list' && (
        <div className="section-card">
          <div className="section-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span>Your Tickets</span>
            <div style={{display:'flex',gap:6}}>
              {['all','open','in_progress','resolved','closed'].map(s=>(
                <button key={s}
                  style={{
                    padding:'2px 10px', borderRadius:12, border:'1px solid',
                    fontSize:11, fontWeight:700, cursor:'pointer',
                    background: filter===s ? 'white':'transparent',
                    color: filter===s ? 'var(--primary)':'rgba(255,255,255,0.75)',
                    borderColor: filter===s ? 'white':'rgba(255,255,255,0.35)',
                  }}
                  onClick={()=>setFilter(s)}>
                  {s==='all'?'All':STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {loading && <Spinner />}
          {error   && <ErrBanner msg={error} />}

          {!loading && filtered.length === 0 && (
            <div style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>
              <div style={{fontSize:40,marginBottom:12}}>🎫</div>
              <div style={{fontWeight:600,marginBottom:6}}>
                {filter==='all' ? 'No tickets yet' : `No ${STATUS_LABEL[filter]?.toLowerCase()} tickets`}
              </div>
              <div style={{fontSize:13}}>
                {filter==='all' ? 'Raise a ticket for any queries, issues or suggestions.' : 'All clear!'}
              </div>
            </div>
          )}

          <div className="ticket-list">
            {filtered.map(t => (
              <div key={t.id} className={`ticket-card ${t.status}`} onClick={()=>openTicket(t)}>
                <div className="ticket-card-top">
                  <div className="ticket-subject">{t.subject}</div>
                  <div className="ticket-meta">
                    <span className={`badge ${PRIORITY_CLASS[t.priority]||'badge-info'}`}>{t.priority}</span>
                    <span className={`badge ${STATUS_CLASS[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                  </div>
                </div>
                <div className="ticket-card-bottom">
                  <span className="ticket-id">#{String(t.id).padStart(5,'0')}</span>
                  <span className="ticket-category">📂 {t.category}</span>
                  <span className="ticket-date">🕐 {fmtDate(t.created_at, true)}</span>
                  {t.reply_count > 0 && (
                    <span style={{fontSize:11.5,color:'var(--primary)'}}>💬 {t.reply_count} {t.reply_count===1?'reply':'replies'}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════ NEW TICKET ══════════════════════ */}
      {view === 'new' && (
        <div className="section-card">
          <div className="section-header">Raise New Support Ticket</div>
          <form className="new-ticket-form" onSubmit={submitTicket}>
            <div className="ticket-form-grid">
              <div className="form-group" style={{gridColumn:'1/-1'}}>
                <label>Subject *</label>
                <input className="form-control" value={newForm.subject} required
                  placeholder="Brief summary of your issue or query"
                  onChange={e=>setNF({...newForm,subject:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select className="form-control" value={newForm.category}
                  onChange={e=>setNF({...newForm,category:e.target.value})}>
                  {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select className="form-control" value={newForm.priority}
                  onChange={e=>setNF({...newForm,priority:e.target.value})}>
                  {PRIORITIES.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group ticket-desc">
                <label>Description *</label>
                <textarea
                  placeholder="Describe your issue, query or suggestion in detail. Include steps to reproduce if it's a bug."
                  value={newForm.description}
                  onChange={e=>setNF({...newForm,description:e.target.value})}
                  required />
              </div>
            </div>

            {/* Info box */}
            <div style={{background:'var(--primary-light)',border:'1px solid #b3d4f0',borderRadius:6,padding:'10px 14px',
                         fontSize:12.5,color:'var(--primary-dark)',marginBottom:16,display:'flex',gap:8,alignItems:'flex-start'}}>
              <span style={{fontSize:16,flexShrink:0}}>ℹ️</span>
              <div>
                <strong>Support Hours:</strong> Mon–Sat, 9 AM – 6 PM IST<br/>
                <strong>Contact:</strong> +91 89783 61752 · info@noris.in · <a href="https://noris.in" target="_blank" rel="noreferrer" style={{color:'var(--primary)'}}>noris.in</a>
              </div>
            </div>

            <div className="action-bar" style={{padding:0}}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? '⏳ Submitting…' : '🚀 Submit Ticket'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={()=>setView('list')}>✕ Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════════ DETAIL VIEW ══════════════════════ */}
      {view === 'detail' && selected && (
        <div className="ticket-detail">
          <div className="ticket-detail-header">
            <div className="tdh-title">#{String(selected.id).padStart(5,'0')} — {selected.subject}</div>
            <div className="tdh-meta">
              <span>📂 {selected.category}</span>
              <span><span className={`badge ${PRIORITY_CLASS[selected.priority]}`}>{selected.priority}</span></span>
              <span><span className={`badge ${STATUS_CLASS[selected.status]}`}>{STATUS_LABEL[selected.status]}</span></span>
              <span>🕐 {fmtDate(selected.created_at, true)}</span>
            </div>
          </div>

          <div className="ticket-detail-body">
            {/* Original description */}
            <div style={{fontWeight:700,fontSize:12,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}>
              Original Message
            </div>
            <div className="td-description">{selected.description}</div>

            {/* Reply thread */}
            {(selected.replies || []).length > 0 && (
              <>
                <div style={{fontWeight:700,fontSize:12,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:10}}>
                  Conversation ({selected.replies.length})
                </div>
                <div className="reply-thread">
                  {(selected.replies||[]).map((r, i) => (
                    <div key={i} className={`reply-bubble ${r.sender_type}`}>
                      <div className={`reply-avatar ${r.sender_type}`}>
                        {r.sender_type==='staff' ? 'N' : (user?.name||'U').charAt(0).toUpperCase()}
                      </div>
                      <div className={`reply-content ${r.sender_type}`}>
                        <div className="reply-text">{r.message}</div>
                        <div className="reply-time">
                          {r.sender_type==='staff' ? '⚡ Noris Support' : '👤 You'} · {fmtDate(r.created_at, true)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Reply composer (only if not closed) */}
            {selected.status !== 'closed' && selected.status !== 'resolved' ? (
              <div className="reply-composer">
                <div style={{fontWeight:700,fontSize:12,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}>
                  Add Reply
                </div>
                <textarea
                  placeholder="Type your message, additional details or follow-up question…"
                  value={replyText} onChange={e=>setRT(e.target.value)} />
                <div className="action-bar" style={{padding:'10px 0 0'}}>
                  <button className="btn btn-primary" onClick={sendReply} disabled={saving||!replyText.trim()}>
                    {saving ? '⏳ Sending…' : '📨 Send Reply'}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={()=>closeTicket(selected.id)}>
                    🔒 Close Ticket
                  </button>
                </div>
              </div>
            ) : (
              <div style={{textAlign:'center',padding:'16px 0',color:'var(--text-muted)',fontSize:13}}>
                <span className={`badge ${STATUS_CLASS[selected.status]}`} style={{fontSize:13,padding:'4px 14px'}}>
                  {STATUS_LABEL[selected.status]}
                </span>
                <div style={{marginTop:8}}>This ticket is {selected.status}.</div>
                {selected.status !== 'closed' && (
                  <button className="btn btn-primary btn-sm" style={{marginTop:10}}
                    onClick={async()=>{
                      try {
                        await api.tickets.updateStatus(selected.id,'open');
                        notify('✅ Ticket reopened');
                        const updated = await api.tickets.fetch(selected.id);
                        setSel(updated); reload();
                      } catch(e){notify('❌ '+e.message);}
                    }}>
                    🔄 Reopen Ticket
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
